<template>
  <div ref="componentRef" class="min-h-screen bg-white">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
      <h1 class="text-3xl sm:text-4xl md:text-5xl font-marketing font-bold text-primary mb-6 sm:mb-8">Checkout</h1>

      <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd" />
          </svg>
          <div class="flex-1">
            <p class="text-sm text-red-800 font-sans font-semibold mb-1">Error</p>
            <p class="text-sm text-red-700 font-sans">{{ error }}</p>
          </div>
          <button @click="error = null" class="text-red-400 hover:text-red-600 ml-2">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <div class="mb-6 sm:mb-8">
        <div class="flex items-center justify-between overflow-x-auto pb-2">
          <div class="flex items-center flex-1 min-w-max">
            <div class="flex items-center"
              :class="needsShipping ? (currentStep >= 1 ? 'text-green-700' : 'text-gray-400') : 'text-gray-300'">
              <div
                class="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 cursor-pointer flex-shrink-0"
                :class="needsShipping ? (currentStep >= 1 ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500') : 'border-gray-200 bg-gray-100'"
                @click="needsShipping && (currentStep = 1)">
                <span v-if="currentStep > 1 && needsShipping"
                  class="text-green-700 font-bold text-sm sm:text-base">✓</span>
                <span v-else-if="needsShipping" class="font-marketing font-bold text-sm sm:text-base">1</span>
                <span v-else class="font-marketing font-bold text-gray-400 text-sm sm:text-base">1</span>
              </div>
              <span class="ml-1.5 sm:ml-2 font-marketing cursor-pointer text-xs sm:text-sm whitespace-nowrap"
                :class="needsShipping ? (currentStep >= 1 ? 'text-green-700' : 'text-gray-400') : 'text-gray-300'"
                @click="needsShipping && (currentStep = 1)">Shipping</span>
            </div>
            <div class="flex-1 h-0.5 mx-2 sm:mx-4 min-w-[20px]"
              :class="needsDeliveryOption ? (currentStep >= 2 ? 'bg-green-700' : 'bg-gray-300') : (currentStep >= 2 ? 'bg-green-700' : 'bg-gray-200')">
            </div>
            <div class="flex items-center"
              :class="needsDeliveryOption ? (currentStep >= 2 ? 'text-green-700' : 'text-gray-400') : 'text-gray-300'">
              <div
                class="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 cursor-pointer flex-shrink-0"
                :class="needsDeliveryOption ? (currentStep >= 2 ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500') : 'border-gray-200 bg-gray-100'"
                @click="needsDeliveryOption && (currentStep = 2)">
                <span v-if="currentStep > 2 && needsDeliveryOption"
                  class="text-green-700 font-bold text-sm sm:text-base">✓</span>
                <span v-else-if="needsDeliveryOption" class="font-marketing font-bold text-sm sm:text-base">2</span>
                <span v-else class="font-marketing font-bold text-gray-400 text-sm sm:text-base">2</span>
              </div>
              <span class="ml-1.5 sm:ml-2 font-marketing cursor-pointer text-xs sm:text-sm whitespace-nowrap"
                :class="needsDeliveryOption ? (currentStep >= 2 ? 'text-green-700' : 'text-gray-400') : 'text-gray-300'"
                @click="needsDeliveryOption && (currentStep = 2)">Delivery</span>
            </div>
            <div class="flex-1 h-0.5 mx-2 sm:mx-4 min-w-[20px]"
              :class="currentStep >= 3 ? 'bg-green-700' : 'bg-gray-300'"></div>
            <div class="flex items-center" :class="currentStep >= 3 ? 'text-green-700' : 'text-gray-400'">
              <div class="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex-shrink-0"
                :class="currentStep >= 3 ? 'border-green-700 bg-green-50' : 'border-gray-300'">
                <span class="font-marketing font-bold text-sm sm:text-base">3</span>
              </div>
              <span class="ml-1.5 sm:ml-2 font-marketing text-xs sm:text-sm whitespace-nowrap">Payment</span>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div class="lg:col-span-2">
          <div v-if="currentStep === 1 && needsShipping" ref="step1Ref"
            class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 relative">
            <div v-if="translatingStep && currentStep === 1"
              class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div class="text-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-2"></div>
                <p class="text-sm text-gray-600 font-sans">Loading...</p>
              </div>
            </div>
            <h2 class="text-xl sm:text-2xl font-marketing font-bold text-primary mb-4 sm:mb-6">
              {{ !currentUser?.id && hasEvents && !hasPhysicalBooks ? 'Contact Information' : 'Shipping Information' }}
            </h2>
            <p v-if="!currentUser?.id && hasEvents && !hasPhysicalBooks" class="text-sm text-gray-600 font-sans mb-4">
              Please provide your contact information so we can send your event tickets via email.
            </p>
            <div class="space-y-4 mb-6">
              <div>
                <label class="block text-sm font-marketing mb-1">Email *</label>
                <input v-model="shippingAddress.email" type="email" placeholder="your@email.com" required
                  class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
              </div>
              <div>
                <label class="block text-sm font-marketing mb-1">Phone Number *</label>
                <input v-model="shippingAddress.phone" type="tel" placeholder="+1 (555) 123-4567" required
                  @blur="validatePhone"
                  class="w-full px-4 py-2 bg-white text-black border rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm"
                  :class="phoneError ? 'border-red-500' : 'border-gray-300'" />
                <p v-if="phoneError" class="text-xs text-red-600 mt-1 font-sans">{{ phoneError }}</p>
              </div>
              <div>
                <label class="block text-sm font-marketing mb-1">Full Name *</label>
                <input v-model="shippingAddress.name" type="text" placeholder="John Doe" required
                  class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
              </div>
              <div>
                <label class="block text-sm font-marketing mb-1">Street Address *</label>
                <input v-model="shippingAddress.street" type="text" placeholder="123 Main St" required
                  class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-marketing mb-1">City *</label>
                  <input v-model="shippingAddress.city" type="text" placeholder="New York" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                </div>
                <div>
                  <label class="block text-sm font-marketing mb-1">State/Province *</label>
                  <input v-model="shippingAddress.state" type="text" placeholder="NY" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-marketing mb-1">ZIP/Postal Code *</label>
                  <input v-model="shippingAddress.zip" type="text" placeholder="10001" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                </div>
                <div>
                  <label class="block text-sm font-marketing mb-1">Country *</label>
                  <input v-model="shippingAddress.country" type="text" placeholder="United States" required
                    @blur="validateCountry"
                    class="w-full px-4 py-2 bg-white text-black border rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm"
                    :class="countryError ? 'border-red-500' : 'border-gray-300'" />
                  <p v-if="countryError" class="text-xs text-red-600 mt-1 font-sans">{{ countryError }}</p>
                </div>
              </div>
            </div>

            <label class="flex items-center mb-6">
              <input v-model="useShippingForBilling" type="checkbox" class="mr-2" />
              <span class="text-sm font-sans text-gray-700">Use shipping address for billing</span>
            </label>

            <div v-if="!useShippingForBilling" class="mb-6">
              <h3 class="text-xl font-marketing font-bold text-primary mb-4">Billing Address</h3>
              <div class="space-y-4">
                <input v-model="billingAddress.name" type="text" placeholder="Full Name" required
                  class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                <input v-model="billingAddress.street" type="text" placeholder="Street Address" required
                  class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input v-model="billingAddress.city" type="text" placeholder="City" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                  <input v-model="billingAddress.state" type="text" placeholder="State" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input v-model="billingAddress.zip" type="text" placeholder="ZIP Code" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                  <input v-model="billingAddress.country" type="text" placeholder="Country" required
                    class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                </div>
              </div>
            </div>

            <button @click="nextStep" :disabled="processingAddresses || countryError || phoneError"
              class="w-full bg-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base">
              {{ processingAddresses ? 'Processing...' : 'Continue to Delivery Options' }}
            </button>
          </div>

          <div v-if="currentStep === 2 && needsDeliveryOption" ref="step2Ref"
            class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 relative">
            <div v-if="translatingStep && currentStep === 2"
              class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div class="text-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-2"></div>
                <p class="text-sm text-gray-600 font-sans">Loading...</p>
              </div>
            </div>
            <h2 class="text-xl sm:text-2xl font-marketing font-bold text-primary mb-4 sm:mb-6">Select Delivery Option
            </h2>
            <div v-if="loadingDeliveryOptions" class="text-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto mb-4"></div>
              <p class="text-gray-600 font-sans">Loading delivery options...</p>
            </div>
            <div v-else class="space-y-4">
              <div v-for="option in availableDeliveryOptions" :key="option.id" @click="selectDeliveryOption(option.id)"
                class="p-3 sm:p-4 border-2 rounded-md transition-colors cursor-pointer"
                :class="selectedDeliveryOption === option.id ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-2">
                      <input type="radio" :checked="selectedDeliveryOption === option.id" class="mr-2" />
                      <h3 class="font-marketing font-semibold text-primary text-lg">{{ option.name }}</h3>
                    </div>
                    <p v-if="option.description" class="text-sm text-gray-600 font-sans mb-2">{{ option.description }}
                    </p>
                    <div v-if="option.info_fields && option.info_fields.length > 0" class="space-y-1 mb-2">
                      <div v-for="(field, idx) in option.info_fields" :key="idx"
                        class="text-sm text-gray-600 font-sans">
                        <strong>{{ field.label }}:</strong> {{ field.value }}
                      </div>
                    </div>
                    <div v-if="option.available_regions && option.available_regions.length > 0"
                      class="flex flex-wrap gap-3 text-sm text-gray-600 font-sans">
                      <span><strong>Regions:</strong> {{ option.available_regions.join(', ') }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="hasEbooks && !hasPhysicalGoods" class="p-4 border-2 border-gray-200 bg-gray-50 rounded-md">
                <div class="flex items-start gap-2">
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-2">
                      <input type="radio" checked disabled class="mr-2" />
                      <h3 class="font-marketing font-semibold text-gray-500">Email</h3>
                    </div>
                    <p class="text-sm text-gray-500 font-sans mb-2">Ebooks and audiobooks are automatically delivered
                      via email after purchase</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
              <button @click="prevStep"
                class="flex-1 bg-gray-300 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-gray-400 transition-colors font-marketing font-semibold text-sm sm:text-base">
                Back
              </button>
              <button @click="nextStep" :disabled="!selectedDeliveryOption || loadingDeliveryOptions"
                class="flex-1 bg-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base">
                Continue to Payment
              </button>
            </div>
          </div>
          <div v-if="currentStep === 2 && !needsDeliveryOption"
            class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6">
            <div class="p-4 border-2 border-gray-200 bg-gray-50 rounded-md">
              <div class="flex items-start gap-2">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <input type="radio" checked disabled class="mr-2" />
                    <h3 class="font-marketing font-semibold text-gray-500">Email</h3>
                  </div>
                  <p class="text-sm text-gray-500 font-sans mb-2">Event tickets are automatically delivered via email
                    after purchase</p>
                </div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
              <button @click="prevStep"
                class="flex-1 bg-gray-300 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-gray-400 transition-colors font-marketing font-semibold text-sm sm:text-base">
                Back
              </button>
              <button @click="nextStep"
                class="flex-1 bg-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold text-sm sm:text-base">
                Continue to Payment
              </button>
            </div>
          </div>

          <div v-if="currentStep === 3" ref="step3Ref"
            class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 relative">
            <div v-if="translatingStep && currentStep === 3"
              class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div class="text-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-2"></div>
                <p class="text-sm text-gray-600 font-sans">Loading...</p>
              </div>
            </div>
            <h2 class="text-xl sm:text-2xl font-marketing font-bold text-primary mb-4 sm:mb-6">{{ isFreeOrder ? 'Payment Method' : 'Select Payment Method' }}</h2>

            <div v-if="isFreeOrder" class="mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-md">
              <div class="flex items-start gap-2">
                <svg class="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd" />
                </svg>
                <p class="text-sm text-green-800 font-sans">
                  This order is free — no payment is required. Click <strong>Complete Purchase</strong> below to
                  receive your items and a full receipt.
                </p>
              </div>
            </div>

            <div class="space-y-4" :class="isFreeOrder ? 'opacity-50 pointer-events-none select-none' : ''">
              <button @click.stop="selectPaymentMethod('stripe')" :disabled="isFreeOrder"
                class="w-full p-3 sm:p-4 border-2 rounded-md transition-colors text-left overflow-hidden"
                :class="selectedPaymentMethod === 'stripe' ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 min-w-0">
                  <div class="flex items-center flex-1 min-w-0 w-full sm:w-auto">
                    <input type="radio" :checked="selectedPaymentMethod === 'stripe'"
                      class="mr-2 sm:mr-3 flex-shrink-0" :disabled="isFreeOrder" />
                    <div class="min-w-0 flex-1">
                      <div class="font-marketing font-semibold text-primary text-sm sm:text-base">Credit/Debit Card
                      </div>
                      <div class="text-xs sm:text-sm text-gray-600 font-sans">Pay with Visa, Mastercard, or other cards
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-6 sm:ml-0">
                    <svg class="w-8 h-5 sm:w-10 sm:h-6" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path :d="siVisa.path" :fill="`#${siVisa.hex}`" />
                    </svg>
                    <svg class="w-8 h-5 sm:w-10 sm:h-6" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path :d="siMastercard.path" :fill="`#${siMastercard.hex}`" />
                    </svg>
                    <svg class="w-8 h-5 sm:w-10 sm:h-6 hidden sm:block" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path :d="siAmericanexpress.path" :fill="`#${siAmericanexpress.hex}`" />
                    </svg>
                    <svg class="w-8 h-5 sm:w-10 sm:h-6 hidden sm:block" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path :d="siDiscover.path" :fill="`#${siDiscover.hex}`" />
                    </svg>
                  </div>
                </div>
              </button>

              <button @click.stop="selectPaymentMethod('paypal')" :disabled="isFreeOrder"
                class="w-full p-3 sm:p-4 border-2 rounded-md transition-colors text-left overflow-hidden"
                :class="selectedPaymentMethod === 'paypal' ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 min-w-0">
                  <div class="flex items-center flex-1 min-w-0 w-full sm:w-auto">
                    <input type="radio" :checked="selectedPaymentMethod === 'paypal'"
                      class="mr-2 sm:mr-3 flex-shrink-0" :disabled="isFreeOrder" />
                    <div class="min-w-0 flex-1">
                      <div class="font-marketing font-semibold text-primary text-sm sm:text-base">PayPal</div>
                      <div class="text-xs sm:text-sm text-gray-600 font-sans">Pay securely with your PayPal account
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center flex-shrink-0 ml-6 sm:ml-0">
                    <img src="/logos/paypal.png" alt="PayPal" class="h-6 sm:h-8 w-auto" />
                  </div>
                </div>
              </button>
            </div>

            <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
              <button @click="prevStep"
                class="flex-1 bg-gray-300 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-gray-400 transition-colors font-marketing font-semibold text-sm sm:text-base">
                Back
              </button>
              <button v-if="isFreeOrder" @click.stop="completeFreeCheckout" :disabled="processing"
                class="flex-1 bg-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base">
                {{ processing ? 'Processing...' : 'Complete Purchase' }}
              </button>
              <button v-else @click.stop="proceedWithPaymentMethod" :disabled="!selectedPaymentMethod || processing"
                class="flex-1 bg-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base">
                {{ processing ? 'Processing...' : 'Continue' }}
              </button>
            </div>

            <div v-if="stripeCheckoutUrl && selectedPaymentMethod === 'stripe'" class="mt-6 text-center">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
              <p class="text-gray-600 font-sans mb-2">Redirecting to Stripe...</p>
              <p class="text-sm text-gray-500 font-sans mb-4">If you are not redirected automatically, <a
                  :href="stripeCheckoutUrl" target="_blank" class="text-accent underline hover:text-green-600">click
                  here</a></p>
              <button @click="cancelStripeRedirect"
                class="mt-4 text-sm text-gray-600 hover:text-gray-800 underline font-sans">
                Cancel and choose different payment method
              </button>
            </div>
          </div>
        </div>

        <div class="lg:col-span-1">
          <div class="lg:sticky lg:top-8 bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
            <h2 class="text-xl sm:text-2xl font-marketing font-bold text-primary mb-4 sm:mb-6">Order Summary</h2>
            <div class="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              <div v-for="item in items" :key="item.id" class="flex gap-3 sm:gap-4">
                <div class="flex-shrink-0">
                  <div class="w-12 sm:w-16 bg-gray-200 rounded-lg overflow-hidden">
                    <img v-if="item.coverImage || item.image" :src="item.coverImage || item.image" :alt="item.title"
                      class="w-full h-auto object-cover aspect-[3/4]" />
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-xs sm:text-sm font-marketing font-semibold text-primary mb-1 truncate"
                    data-no-translate>{{
                      item.title }}</h3>
                  <p class="text-xs sm:text-sm font-marketing font-semibold text-primary" data-no-translate>${{
                    getItemTotal(item).toFixed(2) }}</p>
                </div>
              </div>
            </div>

            <div class="border-t border-gray-300 pt-4 space-y-2 mb-4">
              <div class="flex justify-between text-sm font-sans">
                <span class="text-gray-600">Subtotal</span>
                <span class="text-primary font-semibold" data-no-translate>${{ subtotal.toFixed(2) }}</span>
              </div>
              <div v-if="orderDiscountCode || discountAmount > 0" class="space-y-1">
                <div class="flex justify-between text-sm font-sans">
                  <span class="text-gray-600">Discount <span v-if="orderDiscountCode">(<span>{{
                    orderDiscountCode }}</span>)</span></span>
                  <span v-if="discountAmount > 0" class="text-green-600 font-semibold">-${{
                    discountAmount.toFixed(2) }}</span>
                  <span v-else class="text-gray-500 font-semibold">$0.00</span>
                </div>
                <p v-if="discountDescription" class="text-xs text-gray-500 font-sans italic">
                  {{ discountDescription }}
                </p>
              </div>
              <div
                class="flex justify-between text-lg font-marketing font-bold text-primary pt-2 border-t border-gray-300">
                <span>Total</span>
                <span data-no-translate>${{ total.toFixed(2) }}</span>
              </div>
            </div>

            <a href="/cart" class="block text-center text-accent hover:text-green-600 font-marketing underline text-sm">
              Return to Cart
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_PUBLISHABLE_KEY } from '../config'
import { paymentService } from '../services/payment'
import { userService } from '../services/user'
import { cartService } from '../services/cart'
import { authService } from '../services/auth'
import { publicService } from '../services/public'
import { siVisa, siMastercard, siAmericanexpress, siDiscover } from 'simple-icons'
import { validatePhoneNumber } from '../utils/phoneValidation'
import { cartRequiresAccount } from '../utils/checkoutGuards'
import { useDataTranslation } from '../composables/useDataTranslation'
import { translationService } from '../services/translation'
import { nextTick } from 'vue'

const componentRef = ref(null)
const step1Ref = ref(null)
const step2Ref = ref(null)
const step3Ref = ref(null)
const translatingStep = ref(false)
const { handleLanguageChange } = useDataTranslation(componentRef, ref(false), '.min-h-screen.bg-white')
const stripe = ref(null)

const translateCurrentStep = async () => {
  const currentLang = translationService.getLanguage()
  if (currentLang === 'en') {
    translatingStep.value = false
    return
  }

  translatingStep.value = true

  try {
    await nextTick()

    let stepElement = null
    if (currentStep.value === 1 && step1Ref.value) {
      stepElement = step1Ref.value
    } else if (currentStep.value === 2 && step2Ref.value) {
      stepElement = step2Ref.value
    } else if (currentStep.value === 3 && step3Ref.value) {
      stepElement = step3Ref.value
    }

    if (stepElement) {
      await translationService.translateElement(stepElement)
      await nextTick()
    }
  } catch (error) {
    console.error('Error translating step:', error)
  } finally {
    translatingStep.value = false
  }
}
const processing = ref(false)
const error = ref(null)
const currentStep = ref(1)
const processingAddresses = ref(false)
const useShippingForBilling = ref(true)
const selectedPaymentMethod = ref(null)
const paypalCheckoutUrl = ref(null)
const stripeCheckoutUrl = ref(null)
const currentUser = ref(null)
const selectedDeliveryOption = ref(null)
const deliveryOptions = ref([])
const loadingDeliveryOptions = ref(false)
const countryError = ref(null)
const phoneError = ref(null)

const cartItems = cartService.cartItems
const subtotal = cartService.subtotal
const discountAmount = cartService.discountAmount
const total = cartService.total
const getItemTotal = cartService.getItemTotal
const orderDiscountCode = computed(() => cartService.cartTotals.value.discount_code || cartService.orderDiscountCode.value)
const discountDescription = computed(() => cartService.cartTotals.value.discount_description)
const items = computed(() => {
  const filteredItems = cartItems.value.filter(item => !item.isUnavailable)
  const expandedItems = []

  filteredItems.forEach(item => {
    if (item.eventId && item.ticketNames && item.ticketNames.length > 0) {
      item.ticketNames.forEach((ticketName, index) => {
        expandedItems.push({
          ...item,
          id: `${item.id}-ticket-${index}`,
          title: `${item.title} - ${ticketName}`,
          quantity: 1,
          ticketName: ticketName,
          ticketNames: [ticketName]
        })
      })
    } else {
      expandedItems.push(item)
    }
  })

  return expandedItems
})

const hasPhysicalBooks = computed(() => {
  if (!items.value || items.value.length === 0) return false
  const result = items.value.some(item => {
    if (item.eventId !== null && item.eventId !== undefined) return false
    if (item.bookId === null || item.bookId === undefined) return false
    const format = (item.format || '').toLowerCase().trim()
    if (format === 'ebook' || format === 'audiobook') return false
    return true
  })
  return result
})

const hasPhysicalGoods = computed(() => {
  return items.value.some(item => {
    const format = (item.format || '').toLowerCase()
    if (format === 'ebook' || format === 'audiobook') return false
    return item.bookId !== null || item.eventId !== null
  })
})

const hasEbooks = computed(() => {
  return items.value.some(item => {
    const format = (item.format || '').toLowerCase()
    return format === 'ebook' || format === 'audiobook'
  })
})

const hasEvents = computed(() => {
  return items.value.some(item => {
    return item.eventId !== null
  })
})

const needsShipping = computed(() => {
  if (hasPhysicalBooks.value) return true
  if (!currentUser.value?.id && hasEvents.value) return true
  return false
})

const needsDeliveryOption = computed(() => {
  return hasPhysicalBooks.value
})

const isFreeOrder = computed(() => total.value === 0)

const availableDeliveryOptions = computed(() => {
  if (!needsDeliveryOption.value) {
    return []
  }
  return deliveryOptions.value.filter(option => {
    if (!option.is_active) return false
    return true
  })
})

const selectDeliveryOption = (optionId) => {
  if (needsDeliveryOption.value) {
    selectedDeliveryOption.value = optionId
  }
}

const createFallbackOption = () => {
  return {
    id: 'fallback-standard',
    name: 'Standard Shipping',
    description: 'Standard shipping for physical goods',
    info_fields: [],
    available_regions: ['Worldwide'],
    is_active: true,
    is_default: true
  }
}

const loadDeliveryOptions = async () => {
  loadingDeliveryOptions.value = true
  try {
    const options = await publicService.getActiveDeliveryOptions()

    if (!options || options.length === 0) {
      deliveryOptions.value = [createFallbackOption()]
    } else {
      deliveryOptions.value = options
    }

    if (needsDeliveryOption.value && deliveryOptions.value.length > 0) {
      const defaultOption = deliveryOptions.value.find(opt => opt.is_default) || deliveryOptions.value[0]
      if (defaultOption) {
        selectedDeliveryOption.value = defaultOption.id
      }
    }
  } catch (error) {
    console.error('Failed to load delivery options:', error)
    deliveryOptions.value = [createFallbackOption()]

    if (needsDeliveryOption.value) {
      selectedDeliveryOption.value = 'fallback-standard'
    }
  } finally {
    loadingDeliveryOptions.value = false
  }
}

const shippingAddress = ref({
  name: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: ''
})

const billingAddress = ref({
  name: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: ''
})

onMounted(async () => {
  window.addEventListener('language-changed', handleLanguageChange)

  currentUser.value = authService.getStoredUser()
  if (!currentUser.value && authService.isAuthenticated()) {
    try {
      const result = await authService.getCurrentUser()
      currentUser.value = result
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  const currentUserId = currentUser.value?.id || null

  await cartService.loadCart(currentUserId)

  const availableItems = cartItems.value.filter(item => !item.isUnavailable)
  if (availableItems.length === 0) {
    window.location.href = '/cart'
    return
  }

  // Books (digital or physical) can only be bought by a signed-in account: the
  // purchase is tied to the buyer's library and receipt email. Guests are sent
  // to sign in first (event tickets and donations still allow guest checkout).
  if (cartRequiresAccount(availableItems, currentUser.value)) {
    window.location.href = `/login?next=${encodeURIComponent('/checkout')}`
    return
  }

  const syncResult = await cartService.syncCartWithBackend(currentUserId)
  if (syncResult.discountCleared && syncResult.discountMessage) {
    error.value = syncResult.discountMessage
  }

  if (cartService.orderDiscountCode.value) {
    const validationResult = await cartService.validateStoredDiscount(currentUserId)
    if (validationResult.cleared && validationResult.message) {
      error.value = validationResult.message
    }
  }

  await cartService.calculateCartTotals(currentUserId)

  if (cartService.orderDiscountCode.value && !cartService.cartTotals.value.discount_code) {
    error.value = 'The discount code you applied is no longer valid and has been removed.'
  }

  if (needsShipping.value) {
    await loadUserAddresses()
  }

  await loadDeliveryOptions()

  await new Promise(resolve => setTimeout(resolve, 100))

  const urlParams = new URLSearchParams(window.location.search)
  const stepParam = urlParams.get('step')
  if (stepParam) {
    const step = parseInt(stepParam, 10)
    if (step >= 1 && step <= 3) {
      currentStep.value = step
    } else {
      if (needsShipping.value) {
        currentStep.value = 1
      } else if (needsDeliveryOption.value) {
        currentStep.value = 2
      } else {
        currentStep.value = 3
      }
    }
  } else {
    if (needsShipping.value) {
      currentStep.value = 1
    } else if (needsDeliveryOption.value) {
      currentStep.value = 2
    } else {
      currentStep.value = 3
    }
  }

  if (!STRIPE_PUBLISHABLE_KEY) {
    console.warn('STRIPE_PUBLISHABLE_KEY is not set. Stripe payments will not work.')
    return
  }

  try {
    stripe.value = await loadStripe(STRIPE_PUBLISHABLE_KEY)
  } catch (err) {
    console.error('Failed to initialize Stripe:', err)
    error.value = 'Payment system initialization failed'
  }

  window.addEventListener('language-changed', async () => {
    await handleLanguageChange()
    await translateCurrentStep()
  })

  await nextTick()
  await translateCurrentStep()
})


watch(orderDiscountCode, async (newCode) => {
  if (newCode) {
    const currentUserId = currentUser.value?.id || null
    await cartService.calculateCartTotals(currentUserId)
  }
})

watch(currentStep, async () => {
  await nextTick()
  await translateCurrentStep()
})

onUnmounted(() => {
  window.removeEventListener('language-changed', handleLanguageChange)
})

watch(useShippingForBilling, (val) => {
  if (val) {
    billingAddress.value = { ...shippingAddress.value }
  }
})

watch(shippingAddress, (val) => {
  if (useShippingForBilling.value) {
    billingAddress.value = { ...val }
  }
}, { deep: true })

const loadUserAddresses = async () => {
  if (!currentUser.value?.id) return

  try {
    const result = await userService.getCurrentUser()
    if (result.success && result.data) {
      if (result.data.shipping_address) {
        shippingAddress.value = { ...result.data.shipping_address, email: result.data.email || '', phone: result.data.phone_number || '' }
      } else {
        shippingAddress.value.email = result.data.email || ''
        shippingAddress.value.phone = result.data.phone_number || ''
      }
      if (result.data.billing_address) {
        billingAddress.value = { ...result.data.billing_address }
      }
    }
  } catch (err) {
    console.error('Failed to load user addresses:', err)
  }
}

const validateCountry = async () => {
  if (!hasPhysicalGoods.value) {
    countryError.value = null
    return
  }

  const country = shippingAddress.value.country?.trim()
  if (!country) {
    countryError.value = null
    return
  }

  // For now, we allow shipping to all countries
  // You can add country validation logic here if needed
  countryError.value = null
}

const validatePhone = () => {
  const validation = validatePhoneNumber(shippingAddress.value.phone)
  phoneError.value = validation.error
  return validation.isValid
}

const prevStep = () => {
  if (currentStep.value === 2) {
    currentStep.value = 1
  } else if (currentStep.value === 3) {
    cancelPayment()
    currentStep.value = 2
  }
  error.value = null
  phoneError.value = null
  countryError.value = null
}

const nextStep = async () => {
  if (currentStep.value === 1) {
    const isGuestWithEvents = !currentUser.value?.id && hasEvents.value
    if (needsShipping.value || isGuestWithEvents) {
      if (!shippingAddress.value.name || !shippingAddress.value.email || !shippingAddress.value.phone ||
        !shippingAddress.value.street || !shippingAddress.value.city || !shippingAddress.value.state ||
        !shippingAddress.value.zip || !shippingAddress.value.country) {
        error.value = 'Please fill in all required fields'
        return
      }

      if (!validatePhone()) {
        error.value = phoneError.value || 'Please enter a valid phone number'
        return
      }

      if (countryError.value) {
        error.value = countryError.value
        return
      }

      processingAddresses.value = true
      error.value = null

      try {
        if (currentUser.value?.id) {
          await userService.updateAddresses(
            useShippingForBilling.value ? shippingAddress.value : { ...shippingAddress.value },
            useShippingForBilling.value ? shippingAddress.value : billingAddress.value
          )
        }
        currentStep.value = 2
      } catch (err) {
        error.value = err.response?.data?.error || 'Failed to save addresses'
        console.error('Address submission error:', err)
      } finally {
        processingAddresses.value = false
      }
    } else {
      currentStep.value = 2
    }
  } else if (currentStep.value === 2) {
    if (needsDeliveryOption.value && !selectedDeliveryOption.value) {
      error.value = 'Please select a delivery option'
      return
    }
    if (!hasPhysicalGoods.value && !hasEbooks.value && !hasEvents.value) {
      error.value = 'Your cart is empty'
      return
    }
    currentStep.value = 3
  }

  await nextTick()
  await translateCurrentStep()
}

const selectPaymentMethod = (method) => {
  if (paypalCheckoutUrl.value && method !== 'paypal') {
    cancelPayPalRedirect()
  }
  if (stripeCheckoutUrl.value && method !== 'stripe') {
    cancelStripeRedirect()
  }

  selectedPaymentMethod.value = method
  error.value = null
}

const cancelPayPalRedirect = () => {
  paypalCheckoutUrl.value = null
  processing.value = false
  selectedPaymentMethod.value = null
}

const cancelStripeRedirect = () => {
  stripeCheckoutUrl.value = null
  processing.value = false
  selectedPaymentMethod.value = null
}

const cancelPayment = () => {
  selectedPaymentMethod.value = null
  processing.value = false
  error.value = null
}

const proceedWithPaymentMethod = async () => {
  if (!selectedPaymentMethod.value) {
    error.value = 'Please select a payment method'
    return
  }

  const isGuestWithEvents = !currentUser.value?.id && hasEvents.value
  if (isGuestWithEvents && (!shippingAddress.value.email || !shippingAddress.value.name || !shippingAddress.value.phone)) {
    error.value = 'Please provide your contact information (name, email, and phone) to receive your event tickets'
    return
  }

  processing.value = true
  error.value = null

  try {
    const currentUserId = currentUser.value?.id || null

    const syncResult = await cartService.syncCartWithBackend(currentUserId)
    if (syncResult.removedItems && syncResult.removedItems.length > 0) {
      error.value = 'Some items in your cart are no longer available. Please review your cart.'
      processing.value = false
      return
    }

    if (syncResult.discountCleared) {
      error.value = syncResult.discountMessage || 'Discount code is no longer valid'
      processing.value = false
      return
    }

    const discountCode = cartService.cartTotals.value.discount_code || cartService.orderDiscountCode.value
    if (discountCode && currentUserId) {
      const validationResult = await cartService.validateStoredDiscount(currentUserId)
      if (validationResult.cleared) {
        error.value = validationResult.message || 'Discount code is no longer valid'
        processing.value = false
        return
      }
    }

    await cartService.calculateCartTotals(currentUserId)

    const paymentItemsMap = new Map()

    cartItems.value.filter(item => !item.isUnavailable).forEach(item => {
      if (item.eventId && item.ticketNames && item.ticketNames.length > 0) {
        const ticketNames = item.ticketNames.filter(name => name && name.trim() !== '')
        paymentItemsMap.set(`event-${item.eventId}`, {
          book_id: null,
          event_id: item.eventId,
          quantity: ticketNames.length,
          ticket_holder_names: ticketNames.length > 0 ? ticketNames : null
        })
      } else {
        const ticketNames = item.ticketNames && item.ticketNames.length > 0
          ? item.ticketNames.filter(name => name && name.trim() !== '')
          : null

        paymentItemsMap.set(`item-${item.id}`, {
          book_id: item.bookId || null,
          book_format_id: item.bookFormatId || null,
          event_id: item.eventId || null,
          format: item.format || null,
          quantity: item.quantity || 1,
          ticket_holder_names: ticketNames && ticketNames.length > 0 ? ticketNames : null
        })
      }
    })

    const paymentItems = Array.from(paymentItemsMap.values())

    const finalShippingAddress = useShippingForBilling.value ? shippingAddress.value : shippingAddress.value
    const finalBillingAddress = useShippingForBilling.value ? shippingAddress.value : billingAddress.value

    const finalDiscountCode = cartService.cartTotals.value.discount_code || cartService.orderDiscountCode.value

    if (selectedPaymentMethod.value === 'paypal') {
      const response = await paymentService.createCheckout(
        paymentItems,
        'paypal',
        currentUser.value?.id,
        finalShippingAddress,
        finalBillingAddress,
        finalDiscountCode,
        selectedDeliveryOption.value && selectedDeliveryOption.value !== 'email' ? parseInt(selectedDeliveryOption.value) : null
      )

      if (!response || !response.checkout_url) {
        throw new Error('Invalid response from PayPal. Please try again.')
      }

      paypalCheckoutUrl.value = response.checkout_url
      processing.value = false

      setTimeout(() => {
        if (paypalCheckoutUrl.value) {
          window.location.href = paypalCheckoutUrl.value
        }
      }, 500)
    } else if (selectedPaymentMethod.value === 'stripe') {
      const response = await paymentService.createCheckout(
        paymentItems,
        'stripe',
        currentUser.value?.id,
        finalShippingAddress,
        finalBillingAddress,
        finalDiscountCode,
        selectedDeliveryOption.value && selectedDeliveryOption.value !== 'email' ? parseInt(selectedDeliveryOption.value) : null
      )

      if (!response || !response.checkout_url) {
        throw new Error('Invalid response from Stripe. Please try again.')
      }

      stripeCheckoutUrl.value = response.checkout_url
      processing.value = false

      setTimeout(() => {
        if (stripeCheckoutUrl.value && selectedPaymentMethod.value === 'stripe') {
          window.location.href = stripeCheckoutUrl.value
        }
      }, 500)
    }
  } catch (err) {
    console.error('Payment initialization error:', err)

    let errorMessage = 'Failed to initialize payment'

    if (err.response) {
      if (err.response.data) {
        if (err.response.data.error) {
          errorMessage = err.response.data.error
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        }
      } else if (err.response.status === 400) {
        errorMessage = 'Invalid payment request. Please check your information and try again.'
      } else if (err.response.status === 401) {
        errorMessage = 'Authentication required. Please log in and try again.'
      } else if (err.response.status === 403) {
        errorMessage = 'You do not have permission to complete this payment.'
      } else if (err.response.status === 404) {
        errorMessage = 'Payment service not found. Please contact support.'
      } else if (err.response.status >= 500) {
        errorMessage = 'Payment service is temporarily unavailable. Please try again later.'
      }
    } else if (err.message) {
      errorMessage = err.message
    } else if (err instanceof TypeError && err.message.includes('fetch')) {
      errorMessage = 'Network error. Please check your internet connection and try again.'
    }

    error.value = errorMessage
    processing.value = false
    paypalCheckoutUrl.value = null
    stripeCheckoutUrl.value = null
  }
}

const completeFreeCheckout = async () => {
  processing.value = true
  error.value = null

  try {
    const currentUserId = currentUser.value?.id || null

    const syncResult = await cartService.syncCartWithBackend(currentUserId)
    if (syncResult.removedItems && syncResult.removedItems.length > 0) {
      error.value = 'Some items in your cart are no longer available. Please review your cart.'
      processing.value = false
      return
    }

    await cartService.calculateCartTotals(currentUserId)

    const paymentItemsMap = new Map()
    cartItems.value.filter(item => !item.isUnavailable).forEach(item => {
      if (item.eventId && item.ticketNames && item.ticketNames.length > 0) {
        const ticketNames = item.ticketNames.filter(name => name && name.trim() !== '')
        paymentItemsMap.set(`event-${item.eventId}`, {
          book_id: null,
          event_id: item.eventId,
          quantity: ticketNames.length,
          ticket_holder_names: ticketNames.length > 0 ? ticketNames : null
        })
      } else {
        const ticketNames = item.ticketNames && item.ticketNames.length > 0
          ? item.ticketNames.filter(name => name && name.trim() !== '')
          : null
        paymentItemsMap.set(`item-${item.id}`, {
          book_id: item.bookId || null,
          book_format_id: item.bookFormatId || null,
          event_id: item.eventId || null,
          format: item.format || null,
          quantity: item.quantity || 1,
          ticket_holder_names: ticketNames && ticketNames.length > 0 ? ticketNames : null
        })
      }
    })

    const paymentItems = Array.from(paymentItemsMap.values())
    const finalShippingAddress = useShippingForBilling.value ? shippingAddress.value : shippingAddress.value
    const finalBillingAddress = useShippingForBilling.value ? shippingAddress.value : billingAddress.value
    const finalDiscountCode = cartService.cartTotals.value.discount_code || cartService.orderDiscountCode.value

    const response = await paymentService.createFreeCheckout(
      paymentItems,
      currentUserId,
      finalShippingAddress,
      finalBillingAddress,
      finalDiscountCode,
      selectedDeliveryOption.value && selectedDeliveryOption.value !== 'email' ? parseInt(selectedDeliveryOption.value) : null
    )

    if (response && response.order_number) {
      cartService.clearCart()
      window.location.href = `/checkout-success?order_number=${encodeURIComponent(response.order_number)}`
    } else {
      throw new Error('Invalid response from free checkout')
    }
  } catch (err) {
    console.error('Free checkout error:', err)
    let errorMessage = 'Failed to complete checkout'
    if (err.response?.data?.error) {
      errorMessage = err.response.data.error
    } else if (err.response?.data?.message) {
      errorMessage = err.response.data.message
    } else if (err.message) {
      errorMessage = err.message
    }
    error.value = errorMessage
    processing.value = false
  }
}

</script>

<style scoped></style>
